import PhoneInput, { parsePhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Button from "react-bootstrap/Button";
import Image from "react-bootstrap/Image";
import Select from 'react-select';
import { EditIcon } from "../../../components/SVGIcon";
import { useState, useEffect, useContext } from "react";
import { AppContext } from "../../../contexts/AppContext";
import { getAllCountries, getStatesofSelectedCountry, getCitiesofSelectedState } from "../../../services/countryStateService";
import { singleUser, updateUser, updateProfilePic, updateUserstatus } from "../../../services/userService";
import { useAlert } from "../../../contexts/AlertContext";
import UserStatusDropDown from "../../../components/UserStatusDropDown";

/* For validation */
import * as Yup from "yup";
import { Formik, Form as FormikForm, Field, ErrorMessage } from "formik";
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { getAllTimezones } from "countries-and-timezones";
import { Country } from 'country-state-city';



const validationSchema = Yup.object({
  firstName: Yup.string().trim().required("First name is required"),
  lastName: Yup.string().trim().required("Last name is required"),
  phoneNo: Yup.string()
    .test('is-valid-phone', 'Invalid phone number', function (value) {
      if (!value) return false;
      const phoneNumber = parsePhoneNumberFromString(value);
      return phoneNumber ? phoneNumber.isValid() : false;
    })
    .required("Phone is required"),
  address: Yup.string().trim().required("Street name is required"),
  country: Yup.string().required("Country is required"),
  state: Yup.string().required("State is required"),
  city: Yup.string().required("City is required"),
  zipCode: Yup.string().trim().required("Zip code is required"),
  //   timezone: Yup.string().required("Timezone is required")
});

const PersonalDetails = ({ auth, setAuth }) => {


  const { setProfilePic } = useContext(AppContext);
  const [selectedCountry, setSelectedCountry] = useState({});
  const [countryList, setCountryList] = useState([]);
  const [userData, setUserData] = useState([]);
  const [timezoneOptions, setTimezoneOptions] = useState([]);
  const [selectedTimezone, setSelectedTimezone] = useState(null);
  const [selectedState, setSelectedState] = useState({});
  const [stateList, setStateList] = useState([]);
  const [phoneDetails, setPhoneDetails] = useState(null);
  const [phoneNo, setPhoneNo] = useState("");
  const [selectedCity, setSelectedCity] = useState({});
  const [cityList, setCityList] = useState([]);
  const { showAlert } = useAlert();
  const [profilePic, setProfilePicState] = useState("");

  const [formData, setFormData] = useState({
    // profilePic: "",
    firstName: "",
    lastName: "",
    email: "",
    phoneNo: "",
    address: "",
    country: "",
    state: "",
    city: "",
    zipCode: "",
  });

  useEffect(() => {
    singleUser(auth?.user?.id).then((response) => {
      if (response?.data?.user) {
        const tempUserData = response.data.user;
        setUserData(tempUserData);

        // Set profile pic
        setProfilePicState(
          tempUserData?.profilePic ??
          `https://ui-avatars.com/api/?name=${tempUserData?.firstName}+${tempUserData?.lastName}`
        );

        // Set form data
        setFormData({
          firstName: tempUserData?.firstName,
          lastName: tempUserData?.lastName,
          email: tempUserData?.email,
          phoneNo: (tempUserData.countryCode?.startsWith('+') ? tempUserData.countryCode : '+' + tempUserData.countryCode) + tempUserData.phoneNo || "",
          address: tempUserData?.address,
          country: tempUserData?.country?.name || "",
          state: tempUserData?.state?.name || "",
          city: tempUserData?.city || "",
          zipCode: tempUserData?.zipCode,
        });

        changePhoneNumber((tempUserData?.countryCode?.startsWith('+') ? tempUserData.countryCode : '+' + tempUserData.countryCode) + tempUserData.phoneNo || "");

        if (tempUserData?.country?.code) {
          const countryOption = {
            label: `${tempUserData.country.flag || ''} ${tempUserData.country.name}`,
            value: { name: tempUserData.country.name, code: tempUserData.country.code }
          };
          setSelectedCountry(countryOption);
          getStatesofSelectedCountry(tempUserData.country.code).then((res) => {
            if (res) {
              const stateOptions = res.map(state => ({
                label: state.name,
                value: { name: state.name, code: state.isoCode }
              }));
              setStateList(stateOptions);

              if (tempUserData?.state?.code) {
                const stateOption = stateOptions.find(s => s.value.code === tempUserData.state.code);
                if (stateOption) {
                  setSelectedState(stateOption);

                  // Then load cities
                  getCitiesofSelectedState(tempUserData.country.code, tempUserData.state.code).then((cityRes) => {
                    if (cityRes) {
                      const cityOptions = cityRes.map(city => ({
                        label: city.name,
                        value: city.name
                      }));
                      setCityList(cityOptions);

                      if (tempUserData?.city) {
                        const cityOption = cityOptions.find(c => c.value === tempUserData.city);
                        if (cityOption) setSelectedCity(cityOption);
                      }
                    }
                  });
                }
              }
            }
          });
        }
      }
    });
  }, []);

  /* API Call to get all country list based on selected state and country */
  /* API Call to get all country list */
  useEffect(() => {
    getAllCountries().then((response) => {
      if (response) {
        const priorityCodes = ['US', 'BR'];
        const priorityCountries = response.filter(country => priorityCodes.includes(country.isoCode));
        const otherCountries = response
          .filter(country => !priorityCodes.includes(country.isoCode))
          .sort((a, b) => a.name.localeCompare(b.name));

        const sortedList = [...priorityCountries, ...otherCountries];
        const options = sortedList.map(country => ({
          label: `${country.flag} ${country.name}`,
          value: {
            name: country.name,
            code: country.isoCode
          }
        }));
        setCountryList(options);
      }
    });
  }, []);

  /* API Call to get all state list based on selected state and country */
  useEffect(() => {
    if (selectedCountry?.value) {
      getStatesofSelectedCountry(selectedCountry?.value?.code).then((response) => {
        if (response) {
          const options = response.map(state => ({
            label: state.name,
            value: {
              name: state.name,
              code: state.isoCode
            }
          }));
          setStateList(options);
        }
      });
    }
  }, [selectedCountry]);

  /* API Call to get all city list based on selected state and country */
  useEffect(() => {
    if (selectedCountry?.value?.code && selectedState?.value?.code) {
      getCitiesofSelectedState(
        selectedCountry.value.code,
        selectedState.value.code
      ).then((response) => {
        if (response) {
          const options = response.map(city => ({
            label: city.name,
            value: city.name
          }));
          setCityList(options);
        } else {
          setCityList([]); // Clear if no cities
        }
      }).catch((err) => {
        console.error("Error fetching cities:", err);
        setCityList([]);
      });
    } else {
      setCityList([]);
    }
  }, [selectedCountry, selectedState]);

  const handleLocationChange = (type, newValue, setFieldValue) => {
    if (type === "country") {
      setSelectedCountry(newValue);
      setSelectedState(null);
      setSelectedCity(null);

      setStateList([]);
      setCityList([]);

      // 🔥 Reset Formik values
      setFieldValue("country", newValue?.value?.name || "");
      setFieldValue("state", "");
      setFieldValue("city", "");
    }

    if (type === "state") {
      setSelectedState(newValue);
      setSelectedCity(null);
      setCityList([]);

      // 🔥 Reset city in Formik
      setFieldValue("state", newValue?.value?.name || "");
      setFieldValue("city", "");
    }

    if (type === "city") {
      setSelectedCity(newValue);
      setFieldValue("city", newValue?.value || "");
    }
  };

  const handleFormSubmit = async (values) => {
    const companyDetails = {
      first_name: values.firstName,
      last_name: values.lastName,
      phone_no: phoneDetails?.nationalNumber,
      country_code: phoneDetails?.countryCallingCode,
      status: userData?.status,
      isMakeSameAsAdmin: userData.isMakeSameAsAdmin,
      ...(!userData.isMakeSameAsAdmin
        ? {
          country: values.country,
          state: values.state,
          city: values.city,
          address: values.address,
          zipCode: values.zipCode
        }
        : {})

    };

    updateUser(auth?.user?.id, companyDetails).then((res) => {
      if (res?.message) {
        setAuth({ ...auth, "user": { ...auth.user, firstName: res?.data?.firstName, lastName: res?.data?.lastName } })
      }
      showAlert({
        title: "Users Notification",
        message: res?.message ?? "something went wrong, try again later",
        variant: "success",
      });
    }).catch((error) =>
      showAlert({
        title: "Users Notification",
        message: error?.data?.message ?? "something went wrong, try again later",
        variant: "danger",
      })
    );
  }

  const changePhoneNumber = (value) => {
    console.log(value)
    if (typeof value !== "undefined") {
      const phoneDetailsTemp = parsePhoneNumber(value);
      setPhoneDetails(phoneDetailsTemp);
      setPhoneNo(value);
    }
  };
  const handleProfilePicChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fd = new FormData();
    fd.append("image", file);
    fd.append("user_id", userData.id);

    try {
      const res = await updateProfilePic(userData.id, fd);

      if (res?.data?.profilePic) {
        setProfilePicState(res.data.profilePic);
        setAuth({
          ...auth,
          user: { ...auth.user, profilePic: res.data.profilePic }
        });
      }

      showAlert({
        title: "Users Notification",
        message: res?.data?.message ?? "Profile photo updated successfully",
        variant: "success",
      });
    } catch (err) {
      showAlert({
        title: "Users Notification",
        message: err?.message ?? "Upload failed",
        variant: "danger",
      });
    }
  };

  const handleStatusChange = (val) => {
    if ([1, 2, 3, 4].includes(val)) {
      const apiParams = {
        status: val,
        // user_id: userData.id
      }

      updateUserstatus(apiParams).then((res) => {
        showAlert({
          title: "Users Notification",
          message: res?.message ?? "Status updated successfully",
          variant: "success",
        });
        setUserData((prev) => ({ ...prev, status: val }));
      })
        .catch((error) =>
          showAlert({
            title: "Users Notification",
            message: error?.response?.data?.message ?? "Status failed to update",
            variant: "danger",
          })
        );

    }
  }

  return (
    <>
      <div className="p-4">
        <h2 className="fs-5 fw-semibold text-gray-800 mb-4">
          Profile Photo
        </h2>
        <div className="d-flex align-items-center gap-4">
          <div className="user-profile-box ratio ratio-1x1">
            <Image
              className="rounded-pill object-fit-cover"
              src={profilePic}
              width={120}
              height={120}
              alt="User Photo"
            />

          </div>
          <div className="d-flex align-items-center gap-2">
            <div className="btn-file">
              <input type="file" id="btn-file-input" onChange={handleProfilePicChange} hidden />
              <label
                htmlFor="btn-file-input"
                className="btn btn-md bg-blue-100 border-0 text-blue-600 shadow-none d-flex align-items-center gap-2"
              >
                <EditIcon /> Edit Photo
              </label>
            </div>
            <UserStatusDropDown
              currentVal={userData?.status}
              onStatusChange={handleStatusChange}
            />
          </div>
        </div>
      </div>

      <Formik
        initialValues={formData}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
      >
        {({ setFieldValue, handleChange, errors, values }) => (
          <FormikForm>
            <div className="input-group-item input-group-warpper p-4 mt-3">
              <Row className="gy-4">
                <Form.Group className="col-md-6" controlId="firstName">
                  <Form.Label className="required">First Name</Form.Label>
                  <Field
                    as={Form.Control}
                    name="firstName"
                    placeholder="Enter first name"
                  />
                  <ErrorMessage name="firstName">
                    {(msg) => <Form.Text className="text-danger">{msg}</Form.Text>}
                  </ErrorMessage>
                </Form.Group>

                <Form.Group className="col-md-6" controlId="lastName">
                  <Form.Label className="required">Last Name</Form.Label>
                  <Field
                    as={Form.Control}
                    name="lastName"
                    placeholder="Enter last name"
                  />
                  <ErrorMessage name="lastName">
                    {(msg) => <Form.Text className="text-danger">{msg}</Form.Text>}
                  </ErrorMessage>
                </Form.Group>

                <Form.Group className="col-md-6" controlId="email">
                  <Form.Label className="required">Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={values?.email}
                    placeholder="Enter email"
                    disabled
                  />
                </Form.Group>

                <Form.Group className="col-md-6" controlId="phoneNo">
                  <Form.Label className="required">Phone</Form.Label>
                  {/* <PhoneInput
                    className="form-control"
                    international
                    name="phoneNo"
                    value={values?.phoneNo}
                    onChange={(val) => setFieldValue("phoneNo", val)}
                    placeholder="Enter phone number"
                  /> */}
                  <PhoneInput
                    className="form-control"
                    international
                    name="phoneNo"
                    value={phoneNo}
                    onChange={(e) => {
                      if (typeof e !== "undefined") {
                        changePhoneNumber(e);
                        setFieldValue("phoneNo", e);
                      }
                    }}
                    placeholder="Enter phone number"
                  />
                  <ErrorMessage name="phoneNo">
                    {(msg) => <Form.Text className="text-danger">{msg}</Form.Text>}
                  </ErrorMessage>
                </Form.Group>

                <Form.Group className="col-md-12" controlId="address">
                  <Form.Label className="required">Street Name</Form.Label>
                  <Field
                    disabled={userData.isMakeSameAsAdmin}
                    as={Form.Control}
                    name="address"
                    placeholder="Enter street name"
                  />
                  <ErrorMessage name="address">
                    {(msg) => <Form.Text className="text-danger">{msg}</Form.Text>}
                  </ErrorMessage>
                </Form.Group>

                <Form.Group className="col-md-6" controlId="country">
                  <Form.Label>Country</Form.Label>
                  <Select
                    isDisabled={userData.isMakeSameAsAdmin}
                    className="react-form-control"
                    classNamePrefix="react-control"
                    options={countryList}
                    value={selectedCountry}
                    onChange={(e) => {
                      // setFieldValue("country", e.value.name);
                      setSelectedCountry(e);
                      handleLocationChange("country", e, setFieldValue)
                    }}
                    placeholder="Select a country"
                  />
                  <Field type="hidden" name="country" />
                  <ErrorMessage name="country">
                    {(msg) => <Form.Text className="text-danger">{msg}</Form.Text>}
                  </ErrorMessage>
                </Form.Group>

                <Form.Group className="col-md-6" controlId="state">
                  <Form.Label className="required">State</Form.Label>
                  <Select
                    isDisabled={userData.isMakeSameAsAdmin || !selectedCountry?.value}
                    className="react-form-control"
                    classNamePrefix="react-control"
                    options={stateList}
                    value={selectedState}
                    onChange={(e) => {
                      // setFieldValue("state", e.value.name);
                      handleLocationChange("state", e, setFieldValue);
                    }}
                    placeholder="Select a state"
                  />
                  <Field type="hidden" name="state" />
                  <ErrorMessage name="state">
                    {(msg) => <Form.Text className="text-danger">{msg}</Form.Text>}
                  </ErrorMessage>
                </Form.Group>

                <Form.Group className="col-md-6" controlId="city">
                  <Form.Label className="required">City</Form.Label>
                  <Select
                    isDisabled={userData.isMakeSameAsAdmin || !selectedState?.value}
                    className="react-form-control"
                    classNamePrefix="react-control"
                    options={cityList}
                    value={selectedCity}
                    onChange={(e) => {
                      // setFieldValue("city", e.value);
                      handleLocationChange("city", e, setFieldValue);
                    }}
                    placeholder="Select a city"
                  />
                  <Field type="hidden" name="city" />
                  <ErrorMessage name="city">
                    {(msg) => <Form.Text className="text-danger">{msg}</Form.Text>}
                  </ErrorMessage>
                </Form.Group>

                <Form.Group className="col-md-6" controlId="zipCode">
                  <Form.Label className="required">Zip Code</Form.Label>
                  <Field
                    disabled={userData.isMakeSameAsAdmin}
                    as={Form.Control}
                    name="zipCode"
                    type="text"
                    placeholder="Enter zip code"
                  />
                  <ErrorMessage name="zipCode">
                    {(msg) => <Form.Text className="text-danger">{msg}</Form.Text>}
                  </ErrorMessage>
                </Form.Group>

                {/* <Form.Group className="col-md-6" controlId="timezone">
                  <Form.Label className="required">Timezone</Form.Label>
                  <Select
                    className="react-form-control"
                    classNamePrefix="react-control"
                    options={timezoneOptions}
                    value={selectedTimezone}
                    onChange={(e) => {
                      setFieldValue("timezone", e.value);
                      setSelectedTimezone(e);
                    }}
                    placeholder="Select a timezone"
                  />
                  <Field type="hidden" name="timezone" />
                  <ErrorMessage name="timezone">
                    {(msg) => <Form.Text className="text-danger">{msg}</Form.Text>}
                  </ErrorMessage>
                </Form.Group> */}
              </Row>
            </div>

            {/* Submit Button */}
            <div className="input-group-warpper d-flex align-align-items-center gap-2 p-4 mt-3">
              <Button variant="primary" className="btn-md" type="submit">Save & Update</Button>
            </div>
          </FormikForm>
        )}
      </Formik>
    </>
  );
};

export default PersonalDetails;
